import { HomeInfo, UserInfo, UserStatus } from '/#/store';
import type { ErrorMessageMode } from '/#/axios';
import { defineStore } from 'pinia';
import { store } from '/@/store';
import { RoleEnum } from '/@/enums/roleEnum';
import { PageEnum } from '/@/enums/pageEnum';
import { ROLES_KEY, TOKEN_KEY, USER_INFO_KEY } from '/@/enums/cacheEnum';
import { getAuthCache, setAuthCache } from '/@/utils/auth';
import { GetUserInfoModel, LoginParams } from '/@/api/sys/model/userModel';
import {
  doLogout,
  doLoOff,
  getCartGoodsNum,
  getCurrentUserStatus,
  getUserInfo,
  loginApi,
} from '/@/api/sys/user';
import { useI18n } from '/@/hooks/web/useI18n';
import { useMessage } from '/@/hooks/web/useMessage';
import { router } from '/@/router';
import { usePermissionStore } from '/@/store/modules/permission';
import { RouteRecordRaw } from 'vue-router';
import { PAGE_NOT_FOUND_ROUTE } from '/@/router/routes/basic';
import { h } from 'vue';
import { useLoginState } from '/@/views/sys/login/useLogin';
import { getUnReadMsgCount } from '/@/api/msg';
import { checkService } from '/@/api/notLogin';
// import {setupGlobalTask} from "/@/store/modules/task";

interface UserState {
  userInfo: Nullable<UserInfo>;
  token?: string;
  roleList: RoleEnum[];
  sessionTimeout?: boolean;
  lastUpdateTime: number;
  homeInfo: HomeInfo | null;
  userStatus: UserStatus | null;
  serviceStatus: boolean | undefined;
}

export const useUserStore = defineStore({
  id: 'app-user',
  state: (): UserState => ({
    // user info
    userInfo: null,
    // token
    token: undefined,
    // roleList
    roleList: [],
    // Whether the login expired
    sessionTimeout: false,
    // Last fetch time
    lastUpdateTime: 0,
    /**
     * 首页信息
     */
    homeInfo: null,
    /**
     * 用户状态
     */
    userStatus: null,
    /**
     * 后端接口服务状态
     */
    serviceStatus: undefined,
  }),
  getters: {
    getUserInfo(): UserInfo {
      return this.userInfo || getAuthCache<UserInfo>(USER_INFO_KEY) || {};
    },
    getToken(): string {
      return this.token || getAuthCache<string>(TOKEN_KEY);
    },
    getRoleList(): RoleEnum[] {
      return this.roleList.length > 0 ? this.roleList : getAuthCache<RoleEnum[]>(ROLES_KEY);
    },
    getSessionTimeout(): boolean {
      return !!this.sessionTimeout;
    },
    getLastUpdateTime(): number {
      return this.lastUpdateTime;
    },
    getUserId(): string {
      return localStorage.getItem(`${USER_INFO_KEY}custom_ZKSID`) || '';
    },
    getHomeInfo(): HomeInfo {
      return this.homeInfo || {};
    },
    getUserStatus(): UserStatus {
      return this.userStatus || {};
    },
    getServiceStatus(): boolean {
      return this.serviceStatus || false;
    },
  },
  actions: {
    setUserId(info: string | undefined) {
      info
        ? localStorage.setItem(`${USER_INFO_KEY}custom_ZKSID`, info)
        : localStorage.removeItem(`${USER_INFO_KEY}custom_ZKSID`);
    },
    setToken(info: string | undefined) {
      this.token = info ? info : ''; // for null or undefined value
      setAuthCache(TOKEN_KEY, info);
    },
    setRoleList(roleList: RoleEnum[]) {
      this.roleList = roleList;
      setAuthCache(ROLES_KEY, roleList);
    },
    setUserInfo(info: UserInfo | null) {
      this.userInfo = info;
      this.lastUpdateTime = new Date().getTime();
      this.setUserId(info?.userId);
      setAuthCache(USER_INFO_KEY, info);
    },
    setHomeInfo(info: HomeInfo | null) {
      this.homeInfo = info;
    },
    refreshCartGoodsNum() {
      try {
        getCartGoodsNum().then((res) => {
          this.homeInfo = {
            unreadMessageCount: this.homeInfo?.unreadMessageCount,
            cartGoodsNum: res,
          };
        });
      } catch (e) {
        console.error('刷新购物车数量异常:', e);
      }
    },
    // refreshUnreadMsgCount() {
    //     try {
    //         getUnReadMsgCount().then((res) => {
    //             this.homeInfo = {
    //                 unreadMessageCount: res,
    //                 cartGoodsNum: this.homeInfo?.cartGoodsNum,
    //             }
    //         });
    //     } catch (e) {
    //         console.error('刷新未读消息数异常:', e);
    //     }
    // },
    setUserStatus(info: UserStatus | null) {
      this.userStatus = info;
    },
    setSessionTimeout(flag: boolean) {
      this.sessionTimeout = flag;
    },
    resetState() {
      this.userInfo = null;
      this.token = '';
      this.roleList = [];
      this.sessionTimeout = false;
    },
    /**
     * @description: login
     */
    async login(
      params: LoginParams & {
        goHome?: boolean;
        mode?: ErrorMessageMode;
      },
    ): Promise<GetUserInfoModel | null> {
      try {
        const { goHome = true, mode, ...loginParams } = params;
        const data = await loginApi(loginParams, mode);
        const { token } = data;

        // save token
        this.setToken(token);
        return this.afterLoginAction(goHome);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async afterLoginAction(goHome?: boolean): Promise<GetUserInfoModel | null> {
      // if (!this.getToken) return null;
      if (!this.getToken) {
        this.setToken('fakeToken1');
      }
      // get user info
      const userInfo = await this.getUserInfoAction();

      const sessionTimeout = this.sessionTimeout;
      if (sessionTimeout) {
        this.setSessionTimeout(false);
      } else {
        const permissionStore = usePermissionStore();
        if (!permissionStore.isDynamicAddedRoute) {
          const routes = await permissionStore.buildRoutesAction();
          routes.forEach((route) => {
            router.addRoute(route as unknown as RouteRecordRaw);
          });
          router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw);
          permissionStore.setDynamicAddedRoute(true);
        }
        goHome && (await router.replace(userInfo?.homePath || PageEnum.BASE_HOME));
      }
      return userInfo;
    },
    async checkUserInfoAction() {
      // let userInfo = null as UserInfo | null;
      // try {
      const otoken = await this.getQueryVariable('otoken');
      const res = await getUserInfo({ otoken: otoken });
      // console.log(res)
      this.setUserInfo(res);
      useLoginState().setLoginState(1);
      // } catch (e) {
      //     console.error('获取用户信息异常', e);
      // }
    },
    // 用户登录状态下获取userInfo以及首页消息信息
    async getUserInfoAction(): Promise<UserInfo | null> {
      // try {
      //     getUnReadMsgCount().then((res) => {
      //         const homeInfo = this.getHomeInfo;
      //         homeInfo.unreadMessageCount = res;
      //         this.setHomeInfo(homeInfo);
      //     });
      // } catch (e) {
      //     console.error('获取未读消息数异常', e);
      // }

      // try {
      //     getCartGoodsNum().then((res) => {
      //         const homeInfo = this.getHomeInfo;
      //         homeInfo.cartGoodsNum = res;
      //         this.setHomeInfo(homeInfo);
      //     });
      // } catch (e) {
      //     console.error('获取购物车商品数量异常', e);
      // }

      // try {
      //     await getCurrentUserStatus().then((res) => {
      //         this.setUserStatus(res);
      //     });
      // } catch (e) {
      //     console.error('获取用户状态异常', e);
      // }

      // setupGlobalTask();

      return null;
    },
    //无网络情况
    // async checkServiceStatus(): Promise<any> {
    //     const currentPathname = window.location.pathname;
    //     if (currentPathname.indexOf('serviceError') == -1) {
    //         await checkService().then(() => {
    //             this.serviceStatus = true;
    //         }).catch((error) => {
    //             const {code, message} = error || {};
    //             if (code === 'ECONNABORTED' && message.indexOf('timeout') !== -1) {
    //                 this.serviceStatus = false;
    //                 const origin = window.location.origin;
    //                 window.location.href = origin + '/benefits/serviceError';
    //             }
    //         })
    //     } else {
    //         this.serviceStatus = false;
    //     }
    //     return null;
    // },
    /**
     * @description: logout
     */
    async logout(_, url = '#') {
      try {
        // 登录注销
        await doLogout({ appsource: 'self' }).then((res) => {
          localStorage.removeItem('pt-at-emp');
          localStorage.removeItem('ut');
          this.setSessionTimeout(false);
          this.setUserInfo(null);
          window.location.href = res;
        });
      } catch {
        console.log('注销Token失败');
        localStorage.removeItem('pt-at-emp');
        localStorage.removeItem('ut');
        this.setSessionTimeout(false);
        this.setUserInfo(null);
        window.location.href = url;
      }
      // goLogin && router.push(PageEnum.BASE_LOGIN);
    },
    async logOff(_, url = '#') {
      try {
        // 注销
        await doLoOff().then((res) => {
          localStorage.removeItem('pt-at-emp');
          localStorage.removeItem('ut');
          this.setSessionTimeout(false);
          this.setUserInfo(null);
          window.location.href = res;
        });
      } catch {
        console.log('注销Token失败');
        localStorage.removeItem('pt-at-emp');
        localStorage.removeItem('ut');
        this.setSessionTimeout(false);
        this.setUserInfo(null);
        window.location.href = url;
      }
      // goLogin && router.push(PageEnum.BASE_LOGIN);
    },
    async getQueryVariable(variable) {
      let href = window.location.href;
      let query = href.substring(href.indexOf('?') + 1);
      let vars = query.split('&');
      for (var i = 0; i < vars.length; i++) {
        let pair = vars[i].split('=');
        if (pair[0] == variable) {
          return pair[1];
        }
      }
      return false;
    },

    /**
     * @description: Confirm before logging out
     */
    confirmLoginOut() {
      const { createConfirm } = useMessage();
      const { t } = useI18n();
      createConfirm({
        iconType: 'warning',
        title: () => h('span', t('sys.app.logoutTip')),
        content: () => t('sys.app.logoutMessage'),
        onOk: async () => {
          await this.logout(true);
        },
      });
    },

    /**
     * @description: Confirm before logging out
     */
    confirmLoginOff() {
      const { createConfirm } = useMessage();
      const { t } = useI18n();
      createConfirm({
        iconType: 'warning',
        title: () => h('span', t('sys.app.logoutTip')),
        content: () => h('span', '是否确认注销?'),
        onOk: async () => {
          await this.logOff(true);
        },
      });
    },
  },
});

// Need to be used outside the setup
export function useUserStoreWithOut() {
  return useUserStore(store);
}
